import { ExternalLayout, useToast } from '@melio/penny';
import { useMelioIntl } from '@melio/platform-i18n';
import { ReactNode, useState } from 'react';

import { usePaymentData } from '../../hooks/usePaymentData.hooks';
import { ServerErrorScreen } from '../../shared/screens/server-error';
import { AcceptPaymentWithVirtualCardScreen } from './screens/AcceptPaymentWithVirtualCard/AcceptPaymentWithVirtualCard.screen';

export type AcceptPaymentWithVirtualCardActivityProps = {
  paymentId: string;
  contentHeaderTitle: ReactNode;
  onBack: VoidFunction;
  onDone: () => Promise<void>;
  isGuest: boolean;
};

export const AcceptPaymentWithVirtualCardActivity: React.VFC<AcceptPaymentWithVirtualCardActivityProps> = ({
  onDone,
  contentHeaderTitle,
  onBack,
  paymentId,
  isGuest,
}) => {
  const {
    data: paymentData,
    error: paymentError,
    isLoading: isLoadingPayment,
  } = usePaymentData({ paymentId, isGuest });

  const showToast = useToast();
  const { formatMessage } = useMelioIntl();
  const [isSaving, setIsSaving] = useState(false);

  const onAcceptPayment = async () => {
    try {
      setIsSaving(true);
      // TODO: call create virtual card DM API (ME-56847)
      await onDone();
    } catch (error) {
      showToast({
        type: 'success',
        title: formatMessage('vex.activities.acceptPaymentWithVirtualCard.errors.toast.title'),
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (paymentError) {
    return <ServerErrorScreen />;
  }

  if (!paymentData || isLoadingPayment) {
    return <ExternalLayout isLoading />;
  }

  if (!paymentData.vendor.contactEmail) {
    return <ServerErrorScreen />;
  }

  return (
    <AcceptPaymentWithVirtualCardScreen
      onBack={onBack}
      onDone={onAcceptPayment}
      isSaving={isSaving}
      contentHeaderTitle={contentHeaderTitle}
      payorOrganizationName={paymentData.orgName}
      payorEmail={paymentData.creatorAccountEmail}
      vendorContactEmail={paymentData.vendor.contactEmail}
      vendorId={paymentData.vendor.id}
    />
  );
};

AcceptPaymentWithVirtualCardActivity.displayName = 'AcceptPaymentWithVirtualCardActivity';
